// Teams directory — sourced from Mongo via the Symbols SDK, not from
// Supabase. The `user_profiles.department` / `user_profiles.group_name`
// text columns on governance are legacy; team membership is now
// canonical in Mongo's `Team` + `TeamMember`.
//
// Load once per session into root state:
//   root.teams         Array of { id, name, slug, parentTeam, orgId }
//   root.teamsByEmail  { [lowercase email]: Array<{ id, name, role }> }
//
// Consumers (peopleGroups, PersonCard, MemberProfile, _person mapper)
// read from root.teamsByEmail instead of person.department / .group.

import { getSDK } from '../../shared/functions/sdk.js'

// SDK list-response unwrap — the server returns either `{items}`,
// `{data:{items}}`, `{data:[...]}`, or a bare array depending on the
// endpoint + version. Normalize to a plain array.
const _unwrapList = (r) =>
  r?.items || r?.data?.items || r?.data || (Array.isArray(r) ? r : [])

// Use the shared SDK instance (pointed at the same channel as the rest of
// the workspace — `localhost:8080` in localNext, `next.api.symbols.app`
// in next, etc.). The previous hack here built its own SDK and copied
// the federation bridge token into `symbols_access_token`, which broke
// `/core/*` calls in localNext mode (bridge token is signed by prod, the
// local server uses a different secret). The shared SDK already carries
// the locally-minted token via TokenManager — no per-module override.
const _getSdk = () => {
  if (typeof window === 'undefined') return null
  return getSDK()
}

// Pull every team across every team-org the caller belongs to, plus
// the members of each team AND each org's full member roster. Returns:
//   teams          Array<{ id, name, slug, parentTeam, orgId, orgName }>
//   teamsByEmail   { [email]: Array<{ id, name, slug, orgId, role }> }
//   orgMembersByOrgId  { [orgId]: Set<email> }
//
// `orgMembersByOrgId` is what /people, mention lists, and other
// user-scoped surfaces use to filter the cross-tenant `public.people`
// view down to the active org's membership. Avoids a schema change on
// Supabase's identity tables.
export const loadAllTeams = async (root) => {
  const sdk = await _getSdk()
  if (!sdk) return { teams: [], teamsByEmail: {}, orgMembersByOrgId: {} }
  // Fetch members for EVERY org the user belongs to. Filtering by
  // type (`'team'` only) used to skip the Symbols System home org on
  // some accounts — when the active workspace lives under such an
  // org, /people then renders empty.
  const orgs = (root?.jwtClaims?.orgs || [])
  const teams = []
  const teamsByEmail = {}
  const orgMembersByOrgId = {}
  // Serialize per-org to stay under the 180 req/min free-tier cap.
  // Parallel across orgs bursts 5×(N team members) simultaneous calls,
  // which trips HTTP 429 and silently skips orgs. Sequential adds
  // a small latency hit on cold load but keeps loadAllTeams robust.
  for (const org of orgs) {
    const orgId = org.id
    if (!orgId) continue
    // Org roster — one call per org. Drives the /people filter below.
    // Store as a plain lookup object ({email: true}) not a Set because
    // smbls's root.update() path JSON-clones state and Sets don't
    // survive — the entries become empty {}. Plain objects round-trip.
    try {
      const oRes = await sdk.listOrgMembers(orgId)
      const oMembers = _unwrapList(oRes)
      const map = {}
      for (const m of oMembers) {
        const email = (m?.user?.email || m?.email || '').toLowerCase()
        if (email) map[email] = true
      }
      orgMembersByOrgId[orgId] = map
    } catch (err) {
      console.warn('[teams] listOrgMembers failed for', orgId, err)
      // Leave key unset on failure — filterToActiveOrg treats a
      // missing entry as "show full cross-tenant list" rather than
      // "empty org", so the UI degrades gracefully.
    }

    // Teams inside the org. On failure (rate limit, network, etc.)
    // skip this org and keep iterating — do NOT exit loadAllTeams,
    // otherwise the caller destructures undefined.
    let list = []
    try {
      const res = await sdk.listTeams(orgId)
      list = _unwrapList(res)
    } catch (err) {
      console.warn('[teams] listTeams failed for', orgId, err)
      continue
    }
    for (const t of list) {
      const team = {
        id: t._id || t.id,
        name: t.name,
        slug: t.slug,
        parentTeam: t.parentTeam || null,
        category: t.category || 'active',
        isSystem: !!t.isSystem,
        permissions: t.permissions || [],
        orgId,
        orgName: org.name
      }
      teams.push(team)
      // Member fetching is intentionally deferred — each listTeamMembers
      // is a separate API call and with many teams this bursts past the
      // free-tier cap (180 req/min) and causes 429s. loadTeamMembers()
      // is called lazily when the user opens a team detail view.
    }
  }
  return { teams, teamsByEmail, orgMembersByOrgId }
}

// Lazy-fetch members for a single team. Merges results into
// root.teamsByEmail without stomping other teams' entries. Idempotent —
// a second call for the same teamId is skipped (root.teamMembersLoaded
// tracks which teams have been fetched this session).
//
// Usage: call from the detail view onInit / show transition.
export const loadTeamMembers = async (root, team) => {
  if (!team?.id) return
  // Skip if already loaded this session.
  if (root?.teamMembersLoaded?.[team.id]) return
  const sdk = await _getSdk()
  if (!sdk) return
  try {
    const mRes = await sdk.listTeamMembers(team.orgId, team.id)
    const members = _unwrapList(mRes)
    // Merge new entries into the existing teamsByEmail map without
    // touching entries from other teams (different teamId).
    const next = { ...(root?.teamsByEmail || {}) }
    for (const m of members) {
      const email = (m?.user?.email || m?.email || '').toLowerCase()
      if (!email) continue
      const entry = {
        id: team.id,
        name: team.name,
        slug: team.slug,
        category: team.category,
        orgId: team.orgId,
        role: m?.role || 'member'
      }
      // Avoid duplicate entries for this team on re-open.
      const existing = (next[email] || []).filter((e) => e.id !== team.id)
      next[email] = [...existing, entry]
    }
    root.update({
      teamsByEmail: next,
      teamMembersLoaded: { ...(root?.teamMembersLoaded || {}), [team.id]: true }
    })
  } catch (err) {
    console.warn('[teams] loadTeamMembers failed for', team.id, err)
  }
}

// Convenience — the list of team entries a given person belongs to.
// Case-insensitive by design; call sites pass either `r.email` or a
// freshly-typed string.
export const personTeams = (email, root) => {
  const key = String(email || '').toLowerCase()
  return root?.teamsByEmail?.[key] || []
}

// Primary team label for a person — first entry, or null. Consumers
// that need a single string (row badges, subtitles) call this.
export const primaryTeamName = (email, root) => {
  const teams = personTeams(email, root)
  return teams.length ? teams[0].name : null
}

// ── Write helpers ─────────────────────────────────────────────────
// Thin wrappers around SDK methods. Optimistically update
// root.teamsByEmail on success so the UI reflects the change before
// the next `loadAllTeams()` refresh.

const _indexedTeamsByEmail = (teamsByEmail, email, team, role = 'member') => {
  const key = String(email || '').toLowerCase()
  const entry = { id: team.id, name: team.name, slug: team.slug, orgId: team.orgId, role }
  return { ...teamsByEmail, [key]: [...(teamsByEmail?.[key] || []), entry] }
}

const _removedFromTeamsByEmail = (teamsByEmail, email, teamId) => {
  const key = String(email || '').toLowerCase()
  const next = { ...teamsByEmail }
  next[key] = (next[key] || []).filter((t) => t.id !== teamId)
  if (!next[key].length) delete next[key]
  return next
}

// Add a user to a team. Updates root.teamsByEmail optimistically.
export const addMemberToTeam = async (root, { orgId, teamId, userId, email, role = 'member' }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  await sdk.addTeamMember(orgId, teamId, { userId, role })
  const team = (root?.teams || []).find((t) => t.id === teamId) || { id: teamId, name: '', slug: '', orgId }
  root.update({ teamsByEmail: _indexedTeamsByEmail(root.teamsByEmail || {}, email, team, role) })
}

// Remove a user from a team. Looks up the TeamMember id via a fresh
// `listTeamMembers` (no SDK-side membership cache). Updates
// root.teamsByEmail optimistically.
export const removeMemberFromTeam = async (root, { orgId, teamId, userId, email }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const mRes = await sdk.listTeamMembers(orgId, teamId)
  const members = _unwrapList(mRes)
  const row = members.find((m) => m?.user?.id === userId || m?.user?._id === userId || m?.userId === userId)
  const memberId = row?.id || row?._id
  if (memberId) {
    await sdk.removeTeamMember(orgId, teamId, memberId)
  }
  root.update({ teamsByEmail: _removedFromTeamsByEmail(root.teamsByEmail || {}, email, teamId) })
}

// Create a new team in an org. Returns the created team doc. Also
// appends it to root.teams so the picker's option list updates without
// a full reload.
export const createTeam = async (root, { orgId, name }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const slug = String(name || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const res = await sdk.createTeam(orgId, { name, slug })
  const created = res?.data || res
  const team = {
    id: created._id || created.id,
    name: created.name,
    slug: created.slug,
    parentTeam: created.parentTeam || null,
    category: created.category || 'active',
    isSystem: !!created.isSystem,
    permissions: created.permissions || [],
    orgId,
    orgName: (root?.jwtClaims?.orgs || []).find((o) => o.id === orgId)?.name || ''
  }
  root.update({ teams: [...(root?.teams || []), team] })
  return team
}

// Update the permission set for a team. Hits PATCH /organizations/:orgId
// /teams/:teamId with { permissions: [key, ...] }. Server enforces
// admin.team_permissions (or owner-bypass). Optimistically writes the
// new permissions onto the team row in root.teams so the UI reflects
// the change before the next loadAllTeams.
export const updateTeamPermissions = async (root, { orgId, teamId, permissions }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  const res = await sdk.updateTeam(orgId, teamId, { permissions })
  const updated = res?.data || res
  const nextTeams = (root?.teams || []).map((t) =>
    t.id === teamId ? { ...t, permissions: updated?.permissions || permissions } : t
  )
  root.update({ teams: nextTeams })
  return updated
}

// Invite an email to join a team. On accept, the server creates (or
// re-uses) the user's OrgMember and adds them to the target team.
// Maps to POST /organizations/:orgId/teams/:teamId/invitations via the
// SDK's flat `inviteToTeam` method (alias of createTeamInvitation).
export const inviteToTeam = async (root, { orgId, teamId, email }) => {
  const sdk = await _getSdk()
  if (!sdk) throw new Error('SDK not available')
  if (!email) throw new Error('email required')
  return sdk.inviteToTeam(orgId, teamId, { email })
}

// Return teams scoped to a single org — used by the team picker.
export const teamsInOrg = (orgId, root) =>
  (root?.teams || []).filter((t) => t.orgId === orgId)

// True if the given email is a member of the active org. Used by
// consumers (filterPeople, mention lists) to scope the cross-tenant
// `public.people` view down to the current tenant.
export const isActiveOrgMember = (email, root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) return false
  const set = root?.orgMembersByOrgId?.[orgId]
  if (!set) return false
  return !!(set[String(email || '').toLowerCase()])
}
